import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MobileShell } from "@/components/MobileShell";
import { CategoryTabs } from "@/components/CategoryTabs";
import { ProductCard } from "@/components/ProductCard";
import { Search, ChevronLeft } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function CustomerHome() {
    const { department } = useParams();
    const dep = decodeURIComponent(department || "");

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (dep) sessionStorage.setItem("last_department", dep);
    }, [dep]);

    useEffect(() => {
        (async () => {
            try {
                const [pRes, cRes, dRes] = await Promise.all([
                    api.get("/products", { params: { department: dep, only_available: true } }),
                    api.get("/categories", { params: { department: dep } }),
                    api.get("/departments"),
                ]);
                if (!dRes.data.some((d) => d.name === dep)) {
                    setNotFound(true);
                    return;
                }
                setProducts(pRes.data);
                setCategories(cRes.data);
            } finally {
                setLoading(false);
            }
        })();
    }, [dep]);

    const filtered = useMemo(() => {
        let list = products;
        if (activeCat) {
            list = list.filter((p) => {
                const productCategories = Array.isArray(p.categories) && p.categories.length > 0
                    ? p.categories
                    : (p.category ? [p.category] : []);
                return productCategories.includes(activeCat);
            });
        }
        if (q.trim()) {
            const s = q.toLowerCase();
            list = list.filter(
                (p) =>
                    p.name.toLowerCase().includes(s) ||
                    (p.description || "").toLowerCase().includes(s)
            );
        }
        return list;
    }, [products, activeCat, q]);

    if (notFound) return <Navigate to="/" replace />;

    return (
        <MobileShell>
            <Link
                to="/"
                data-testid="back-to-departments"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground py-2 hover:text-foreground"
            >
                <ChevronLeft className="h-4 w-4" />
                Home
            </Link>

            <header className="pt-2 pb-6 md:pt-6 md:pb-10" data-testid="storefront-header">               
                <h1 className="font-serif text-4xl md:text-6xl leading-none text-foreground">
                    {dep}
                </h1>
                <div className="relative mt-6 md:mt-8 md:max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        data-testid="search-input"
                        placeholder={`Search in ${dep}…`}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-10 h-12 rounded-full bg-surface/60 border-border/50 focus-visible:ring-primary"
                    />
                </div>
            </header>

            <CategoryTabs categories={categories} active={activeCat} onChange={setActiveCat} />

            <section className="mt-6 md:mt-10" data-testid="product-grid">
                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {[...Array(8)].map((_, i) => (
                            <div
                                key={i}
                                className="aspect-[3/4] rounded-3xl bg-surface/60 animate-pulse"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        data-testid="empty-products"
                        className="text-center py-20 text-muted-foreground"
                    >
                        <p className="font-serif text-xl">Nothing here yet.</p>
                        <p className="text-sm mt-2">Try a different category or search.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {filtered.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                )}
            </section>
        </MobileShell>
    );
}
